import React, { useCallback, useMemo } from 'react';
import ClearFiltersButton from '../../components/common/ClearFiltersButton/ClearFiltersButton';
import DatePresetFilter from '../../components/common/DatePresetFilter/DatePresetFilter';
import FilterBar from '../../components/common/FilterBar/FilterBar';
import FilterChipsBar from '../../components/common/FilterChipsBar/FilterChipsBar';
import type { FilterChipEntry } from '../../components/common/FilterChipsBar/FilterChipsBar';
import {
  buildDateRangeFilterChip,
  buildOptionFilterChips,
  compactFilterChips,
} from '../../components/common/FilterChipsBar/buildFilterChips';
import FilterGroup from '../../components/common/FilterGroup/FilterGroup';
import MultiSelectFilter from '../../components/common/MultiSelectFilter/MultiSelectFilter';
import type { MultiSelectOption } from '../../components/common/MultiSelectFilter/MultiSelectFilter';
import RecordCount from '../../components/common/RecordCount/RecordCount';
import SearchFilterBar from '../../components/common/SearchFilterBar/SearchFilterBar';
import {
  COMMERCIAL_CANCELLATION_REASON_FILTER_OPTIONS,
  COMMERCIAL_CLASSIFICATION_FILTER_OPTIONS,
  COMMERCIAL_INVESTMENT_INTEREST_FILTER_OPTIONS,
  COMMERCIAL_LEAD_TEMPERATURE_FILTER_OPTIONS,
  COMMERCIAL_MAIN_GOAL_FILTER_OPTIONS,
  COMMERCIAL_OWNER_UNSET_FILTER_VALUE,
  COMMERCIAL_PAID_STATUS_FILTER_OPTIONS,
  COMMERCIAL_PAYMENT_METHOD_FILTER_OPTIONS,
  COMMERCIAL_PERSONA_FILTER_OPTIONS,
  COMMERCIAL_STAGE_FILTER_OPTIONS,
  COMMERCIAL_START_TIME_FILTER_OPTIONS,
} from '../../constants/commercialFilters';
import {
  COMMERCIAL_JOINED_AT_DATE_PRESETS,
  detectDatePreset,
  formatDatePresetShort,
} from '../../constants/datePresets';
import type { QueryFilterValues } from '../../hooks/useQueryFilters';
import { useMarketingProjects } from '../../hooks/useMarketingProjects';
import type { LeadPersonaType, LeadTemperatureType } from '../../types';
import QueroComecarCopyLinkButton from './QueroComecarCopyLinkButton';
import { COMMERCIAL_QUERY_FILTER_SCHEMA } from './commercialQueryFilters';
import type { CommercialListingKey } from './commercialNavigation';
import styles from './Commercial.module.css';

type CommercialQueryFilters = QueryFilterValues<typeof COMMERCIAL_QUERY_FILTER_SCHEMA>;
type CommercialFilterKey = Exclude<keyof CommercialQueryFilters, 'page'>;

interface CommercialFiltersProps {
  filters: CommercialQueryFilters;
  loading: boolean;
  totalRecords: number;
  hasActiveFilters: boolean;
  setFilter: <K extends CommercialFilterKey>(
    key: K,
    value: CommercialQueryFilters[K] | ((current: CommercialQueryFilters[K]) => CommercialQueryFilters[K]),
  ) => void;
  setFilters: (patch: Partial<CommercialQueryFilters>) => void;
  onClearFilters: () => void;
  ownerOptions: MultiSelectOption[];
  hideProjectFilter?: boolean;
  listingMode?: CommercialListingKey;
}

const DATE_REGISTRO_PLACEHOLDER = 'Data';

function formatJoinedAtChipLabel(start: string, end: string): string {
  const preset = detectDatePreset(start, end, COMMERCIAL_JOINED_AT_DATE_PRESETS);
  const presetOption = COMMERCIAL_JOINED_AT_DATE_PRESETS.find((option) => option.value === preset);
  const selectionLabel = presetOption && preset !== 'custom'
    ? presetOption.label
    : `${formatDatePresetShort(start)} – ${formatDatePresetShort(end)}`;

  return `${DATE_REGISTRO_PLACEHOLDER}: ${selectionLabel}`;
}

function removeMultiValue(
  values: string[],
  valueToRemove: string,
): string[] {
  return values.filter((value) => value !== valueToRemove);
}

const CommercialFilters: React.FC<CommercialFiltersProps> = ({
  filters,
  loading,
  totalRecords,
  hasActiveFilters,
  setFilter,
  setFilters,
  onClearFilters,
  ownerOptions,
  hideProjectFilter = false,
  listingMode = 'leads',
}) => {
  const {
    searchName,
    classifications,
    leadTemperatureTypes,
    personaTypes,
    cancellationReasons,
    investmentInterests,
    startTimeExpectations,
    mainGoals,
    commercialStages,
    paidStatuses,
    paymentMethods,
    joinedAtStart,
    joinedAtEnd,
    projectCodes,
    commercialOwnerIds,
  } = filters;
  const projects = useMarketingProjects();
  const projectFilterOptions = useMemo(
    () => projects.map((project) => ({
      value: project.code,
      label: project.code,
      color: project.colors.background,
    })),
    [projects],
  );
  const ownerFilterOptions = useMemo<MultiSelectOption[]>(
    () => [
      { value: COMMERCIAL_OWNER_UNSET_FILTER_VALUE, label: 'Sem responsável' },
      ...ownerOptions,
    ],
    [ownerOptions],
  );

  const handleJoinedAtChange = useCallback((start: string, end: string) => {
    setFilters({ joinedAtStart: start, joinedAtEnd: end });
  }, [setFilters]);

  const filterChips: FilterChipEntry[] = useMemo(() => compactFilterChips(
    hideProjectFilter
      ? []
      : buildOptionFilterChips({
        filterKey: 'projectCodes',
        values: projectCodes,
        options: projectFilterOptions,
        labelPrefix: 'Projeto',
        onRemove: (value) => setFilter(
          'projectCodes',
          (current) => removeMultiValue(current, value),
        ),
      }),
    buildOptionFilterChips({
      filterKey: 'classifications',
      values: classifications,
      options: COMMERCIAL_CLASSIFICATION_FILTER_OPTIONS,
      onRemove: (value) => setFilter(
        'classifications',
        (current) => removeMultiValue(current, value),
      ),
    }),
    buildOptionFilterChips({
      filterKey: 'leadTemperatureTypes',
      values: leadTemperatureTypes,
      options: COMMERCIAL_LEAD_TEMPERATURE_FILTER_OPTIONS,
      onRemove: (value) => setFilter(
        'leadTemperatureTypes',
        (current) => removeMultiValue(current, value) as LeadTemperatureType[],
      ),
    }),
    buildOptionFilterChips({
      filterKey: 'personaTypes',
      values: personaTypes,
      options: COMMERCIAL_PERSONA_FILTER_OPTIONS,
      onRemove: (value) => setFilter(
        'personaTypes',
        (current) => removeMultiValue(current, value) as LeadPersonaType[],
      ),
    }),
    listingMode === 'declined'
      ? []
      : buildOptionFilterChips({
        filterKey: 'commercialStages',
        values: commercialStages,
        options: COMMERCIAL_STAGE_FILTER_OPTIONS,
        onRemove: (value) => setFilter(
          'commercialStages',
          (current) => removeMultiValue(current, value),
        ),
      }),
    buildOptionFilterChips({
      filterKey: 'commercialOwnerIds',
      values: commercialOwnerIds,
      options: ownerFilterOptions,
      labelPrefix: 'Responsável',
      onRemove: (value) => setFilter(
        'commercialOwnerIds',
        (current) => removeMultiValue(current, value),
      ),
    }),
    buildDateRangeFilterChip({
      filterKey: 'joinedAt',
      start: joinedAtStart,
      end: joinedAtEnd,
      label: formatJoinedAtChipLabel(joinedAtStart, joinedAtEnd),
      onRemove: () => setFilters({ joinedAtStart: '', joinedAtEnd: '' }),
    }),
    listingMode === 'declined'
      ? []
      : buildOptionFilterChips({
        filterKey: 'paidStatuses',
        values: paidStatuses,
        options: COMMERCIAL_PAID_STATUS_FILTER_OPTIONS,
        labelPrefix: 'Pago',
        onRemove: (value) => setFilter(
          'paidStatuses',
          (current) => removeMultiValue(current, value),
        ),
      }),
    listingMode === 'declined'
      ? []
      : buildOptionFilterChips({
        filterKey: 'paymentMethods',
        values: paymentMethods,
        options: COMMERCIAL_PAYMENT_METHOD_FILTER_OPTIONS,
        labelPrefix: 'Forma de pagamento',
        onRemove: (value) => setFilter(
          'paymentMethods',
          (current) => removeMultiValue(current, value),
        ),
      }),
    buildOptionFilterChips({
      filterKey: 'investmentInterests',
      values: investmentInterests,
      options: COMMERCIAL_INVESTMENT_INTEREST_FILTER_OPTIONS,
      onRemove: (value) => setFilter(
        'investmentInterests',
        (current) => removeMultiValue(current, value),
      ),
    }),
    buildOptionFilterChips({
      filterKey: 'startTimeExpectations',
      values: startTimeExpectations,
      options: COMMERCIAL_START_TIME_FILTER_OPTIONS,
      onRemove: (value) => setFilter(
        'startTimeExpectations',
        (current) => removeMultiValue(current, value),
      ),
    }),
    buildOptionFilterChips({
      filterKey: 'mainGoals',
      values: mainGoals,
      options: COMMERCIAL_MAIN_GOAL_FILTER_OPTIONS,
      onRemove: (value) => setFilter(
        'mainGoals',
        (current) => removeMultiValue(current, value),
      ),
    }),
    buildOptionFilterChips({
      filterKey: 'cancellationReasons',
      values: cancellationReasons,
      options: COMMERCIAL_CANCELLATION_REASON_FILTER_OPTIONS,
      onRemove: (value) => setFilter(
        'cancellationReasons',
        (current) => removeMultiValue(current, value),
      ),
    }),
  ), [
    cancellationReasons,
    projectCodes,
    projectFilterOptions,
    classifications,
    commercialOwnerIds,
    commercialStages,
    investmentInterests,
    joinedAtEnd,
    joinedAtStart,
    leadTemperatureTypes,
    mainGoals,
    ownerFilterOptions,
    paidStatuses,
    paymentMethods,
    personaTypes,
    setFilter,
    setFilters,
    startTimeExpectations,
    hideProjectFilter,
    listingMode,
  ]);

  return (
    <>
      <FilterBar gap="sm" pushFirstChild className={styles['filter-bar']}>
        <div className={styles['leading-tools']}>
          {listingMode !== 'declined' && <QueroComecarCopyLinkButton />}
          <RecordCount
            label="Alunas:"
            count={totalRecords}
            className={styles['record-count']}
          />
        </div>
        <FilterGroup className={styles['search-filter-group']}>
          <SearchFilterBar
            searchValue={searchName}
            onSearchChange={(value) => setFilter('searchName', value)}
            searchPlaceholder="Pesquisar"
            loading={loading}
          />
        </FilterGroup>
        <FilterGroup>
          <MultiSelectFilter
            placeholder="Tag"
            clearButtonLabel="Limpar tags"
            hideChips
            showSelectionCount={false}
            dropdownClassName={styles['multi-select-dropdown']}
            groups={[
              ...(!hideProjectFilter && projectFilterOptions.length > 0
                ? [{
                    title: 'Projeto',
                    values: projectCodes,
                    onChange: (values: string[]) => setFilter('projectCodes', values),
                    options: projectFilterOptions,
                  }]
                : []),
              {
                title: 'Jornada',
                values: classifications,
                onChange: (values) => setFilter('classifications', values),
                options: COMMERCIAL_CLASSIFICATION_FILTER_OPTIONS,
              },
              {
                title: 'Temperatura',
                values: leadTemperatureTypes,
                onChange: (values) => setFilter(
                  'leadTemperatureTypes',
                  values as LeadTemperatureType[],
                ),
                options: COMMERCIAL_LEAD_TEMPERATURE_FILTER_OPTIONS,
              },
              {
                title: 'Persona',
                values: personaTypes,
                onChange: (values) => setFilter('personaTypes', values as LeadPersonaType[]),
                options: COMMERCIAL_PERSONA_FILTER_OPTIONS,
              },
            ]}
          />
        </FilterGroup>
        {listingMode !== 'declined' && (
          <FilterGroup>
            <MultiSelectFilter
              values={commercialStages}
              onChange={(values) => setFilter('commercialStages', values)}
              options={COMMERCIAL_STAGE_FILTER_OPTIONS}
              placeholder="Clint"
              clearButtonLabel="Limpar estágio clint"
              hideChips
              showSelectionCount={false}
              dropdownClassName={styles['multi-select-dropdown']}
            />
          </FilterGroup>
        )}
        <FilterGroup>
          <MultiSelectFilter
            values={commercialOwnerIds}
            onChange={(values) => setFilter('commercialOwnerIds', values)}
            options={ownerFilterOptions}
            placeholder="Responsável"
            clearButtonLabel="Limpar responsável"
            hideChips
            showSelectionCount={false}
            dropdownClassName={styles['multi-select-dropdown']}
          />
        </FilterGroup>
        <FilterGroup>
          <DatePresetFilter
            dateStart={joinedAtStart}
            dateEnd={joinedAtEnd}
            onChange={handleJoinedAtChange}
            placeholder={DATE_REGISTRO_PLACEHOLDER}
            presetOptions={COMMERCIAL_JOINED_AT_DATE_PRESETS}
            allowEmpty
            keepPlaceholder
          />
        </FilterGroup>
        {listingMode !== 'declined' && (
          <FilterGroup>
            <MultiSelectFilter
              placeholder="Pagamento"
              clearButtonLabel="Limpar pagamento"
              hideChips
              showSelectionCount={false}
              dropdownClassName={styles['multi-select-dropdown']}
              groups={[
                {
                  title: 'Pago',
                  values: paidStatuses,
                  onChange: (values) => setFilter('paidStatuses', values),
                  options: COMMERCIAL_PAID_STATUS_FILTER_OPTIONS,
                },
                {
                  title: 'Forma de pagamento',
                  values: paymentMethods,
                  onChange: (values) => setFilter('paymentMethods', values),
                  options: COMMERCIAL_PAYMENT_METHOD_FILTER_OPTIONS,
                },
              ]}
            />
          </FilterGroup>
        )}
        <FilterGroup>
          <MultiSelectFilter
            placeholder="Qualificação"
            clearButtonLabel="Limpar qualificação"
            hideChips
            showSelectionCount={false}
            dropdownClassName={styles['multi-select-dropdown']}
            groups={[
              {
                title: 'Investimento',
                values: investmentInterests,
                onChange: (values) => setFilter('investmentInterests', values),
                options: COMMERCIAL_INVESTMENT_INTEREST_FILTER_OPTIONS,
              },
              {
                title: 'Momento',
                values: startTimeExpectations,
                onChange: (values) => setFilter('startTimeExpectations', values),
                options: COMMERCIAL_START_TIME_FILTER_OPTIONS,
              },
              {
                title: 'Objetivo',
                values: mainGoals,
                onChange: (values) => setFilter('mainGoals', values),
                options: COMMERCIAL_MAIN_GOAL_FILTER_OPTIONS,
              },
            ]}
          />
        </FilterGroup>
        <FilterGroup>
          <MultiSelectFilter
            values={cancellationReasons}
            onChange={(values) => setFilter('cancellationReasons', values)}
            options={COMMERCIAL_CANCELLATION_REASON_FILTER_OPTIONS}
            placeholder="Desistência"
            clearButtonLabel="Limpar desistência"
            hideChips
            showSelectionCount={false}
            dropdownClassName={styles['multi-select-dropdown']}
          />
        </FilterGroup>
        {hasActiveFilters && (
          <ClearFiltersButton onClick={onClearFilters} />
        )}
      </FilterBar>

      <FilterChipsBar chips={filterChips} />
    </>
  );
};

export default CommercialFilters;
